import { useIntl } from 'react-intl';
import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { createColumnHelper } from '@tanstack/react-table';
import { commonMessages } from 'src/utils/messages';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { AccountingHeading } from 'src/features/accountingHeaders/utils/types';
import { accountingHeadersMessages } from 'src/features/accountingHeaders/utils/messages';
import { ACCOUNTING_HEADER_TYPE_BADGE_MAP } from 'src/features/accountingHeaders/utils/constants';
import { useListAccountingHeadings } from 'src/features/accountingHeaders/api/useListAccountingHeadings';
import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import CreateAccountingHeaderFormModal from 'src/features/accountingHeaders/components/forms/CreateAccountingHeaderFormModal';
import CellAccountingHeaderActions from 'src/features/accountingHeaders/components/tables/CellAccountingHeaderActions';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const AccountingHeadersTable = () => {
    const { formatMessage } = useIntl();
    const permissionManageAccountingHeadings = useHasPermissions([PERMISSION.MANAGE_ACCOUNTING_HEADINGS]);
    const permissionAccessBillingEntities = useHasPermissions([PERMISSION.ACCESS_BILLING_ENTITIES]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(accountingHeadersMessages.accountingHeaders));
        return removeTitle;
    }, [removeTitle, setTitle, formatMessage]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: accountingHeadingData,
        isLoading,
        isError,
    } = useListAccountingHeadings({
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<AccountingHeading>();

    const columns = [
        columnHelper.accessor('billing_entity.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: (ctx) => {
                const billingEntityId = ctx.row.original.billing_entity.id;
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/billing-entity/${billingEntityId}`}
                        hasPermissions={permissionAccessBillingEntities}
                    />
                );
            },
            meta: {
                title: formatMessage(accountingHeadersMessages.billingEntity),
            },
        }),
        columnHelper.accessor('name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('account', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(accountingHeadersMessages.account),
            },
        }),
        columnHelper.accessor('partide', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(accountingHeadersMessages.partide),
            },
        }),
        columnHelper.accessor('type', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: ACCOUNTING_HEADER_TYPE_BADGE_MAP,
            },
        }),
        columnHelper.accessor('parent.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(accountingHeadersMessages.parent),
            },
        }),
        ...(permissionManageAccountingHeadings
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellAccountingHeaderActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];
    return (
        <DataTable<AccountingHeading, unknown>
            columns={columns}
            data={accountingHeadingData}
            semantics={{
                title: formatMessage(accountingHeadersMessages.accountingHeaders),
                entryName: formatMessage(accountingHeadersMessages.header).toLowerCase(),
                entryNames: formatMessage(accountingHeadersMessages.headers).toLowerCase(),
                placeholder: formatMessage(accountingHeadersMessages.accountingHeaders),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageAccountingHeadings && { newEntry: CreateAccountingHeaderFormModal }),
            }}
        />
    );
};

export default AccountingHeadersTable;
